import { Alert, Button, Card, CardBody, Col, Row } from 'react-bootstrap';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useModal } from '../../hooks/useModal';
import { useRmxServiceApi } from '../api';
import type { Activity } from '../Service.types';
import type { AxiosError } from 'axios';
import { RmxServiceSampleModal, type RmxServiceSampleModalParam } from '../RmxServiceSampleModal/RmxServiceSampleModal';
import { QrCodeScannerModal } from '../../utils/QrCodeScannerModal/QrCodeScannerModal';
import { Tabs } from '../../utils/Tabs/Tabs';
import { DataTable } from '../../shared-compat/DataTable/DataTable';
import { useSampleTables } from '../SamplesTable/SamplesTable';
import { useMutation } from '@tanstack/react-query';
import { reportException } from '../../../util/sentryProvider';

interface Props {
  enabled: boolean;
  activity: Activity;
}
export function RmxServiceSample({ enabled, activity }: Props) {
  const codeScannerModal = useModal();
  const api = useRmxServiceApi();
  const sampleModal = useModal<RmxServiceSampleModalParam>();
  const [tab, setTab] = React.useState('samples');
  const { pendingSamplesTable, sampleDataTable } = useSampleTables({
    filterByAssetId: activity.assetId,
    enableSampleDataTable: tab === 'sampleHistory',
    filterOutReviewed: true
  });

  const insertScannedSample = useMutation({
    mutationFn: async (scannedBarcodeValue: string) => {
      await api.insertScannedSample(scannedBarcodeValue, activity.id, activity.assetId);
    },
    onError: (e: AxiosError) => {
      if (e.response?.status === 409) {
        alert('This sample has already been scanned');
      } else reportException(e);
    }
  });

  async function handleSampleScan(scannedBarcodeValue: string) {
    // if raw value is 11 characters long then we assume it's a valid sample id
    if (scannedBarcodeValue.length === 11) {
      if (sampleDataTable.data?.find((s) => s.barCodeId === scannedBarcodeValue)) {
        alert('This sample has already been scanned');
        return;
      }
      await insertScannedSample.mutateAsync(scannedBarcodeValue);
    }
    await pendingSamplesTable.refetch();
  }

  return (
    <div style={{ minHeight: '415px', overflowY: 'auto', overflowX: 'hidden' }}>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <FontAwesomeIcon icon={faInfoCircle} /> Scanning a sample will associate it with this activity. If the sample has already been scanned, you will
              be notified.
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row className={'mb-3'}>
        <Col>
          <Card style={{ width: '100%' }}>
            <CardBody style={{ display: 'flex', justifyContent: 'center' }}>
              {
                <QrCodeScannerModal
                  state={codeScannerModal}
                  onScan={async (result) => {
                    await handleSampleScan(result.rawValue);
                  }}
                />
              }
              {!codeScannerModal.isOpen ? (
                <Button disabled={!enabled} variant={'secondary'} onClick={() => codeScannerModal.open(undefined)}>
                  Click here to scan a sample
                </Button>
              ) : (
                <></>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {insertScannedSample.isError && <Alert variant={'danger'}>Failed to upload scanned sample. Support has been contacted.</Alert>}

      <Tabs
        fill={true}
        activeTab={tab}
        setActiveTab={setTab}
        tabs={[
          {
            key: 'samples',
            title: 'Pending Samples',
            content: (
              <Card>
                {pendingSamplesTable.data?.length === 0 ? (
                  <CardBody style={{ display: 'flex', justifyContent: 'center' }}>No Pending Samples Found</CardBody>
                ) : (
                  <DataTable table={pendingSamplesTable} />
                )}
              </Card>
            )
          },
          {
            key: 'sampleHistory',
            title: 'Sample History',
            content: (
              <Card>
                {sampleDataTable.data?.length === 0 ? (
                  <CardBody style={{ display: 'flex', justifyContent: 'center' }}>No Sample History Found</CardBody>
                ) : (
                  <DataTable
                    table={sampleDataTable}
                    onRowClick={(row) => {
                      row.id &&
                        void sampleModal.open({
                          resource: { sampleData: row }
                        });
                    }}
                  />
                )}
              </Card>
            )
          }
        ]}
      />
      {sampleModal.isOpen && <RmxServiceSampleModal state={sampleModal} refetchSampleHistory={sampleDataTable.refetch} reviewDisabled={true} />}
    </div>
  );
}
